export default function Terms() {
  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-20 sm:pt-28 pb-24" data-testid="page-terms">
      <div className="eyebrow text-[var(--pine)] mb-5">Legal</div>
      <h1 className="font-display text-5xl sm:text-7xl leading-[0.95] mb-10">Terms &amp; conditions</h1>
      <p className="text-[var(--ink-soft)] text-sm mb-12">Last updated: January 2026</p>

      <div className="space-y-10 text-[var(--ink)] leading-relaxed">
        <Section title="The short version">
          We're a small Riga-based booking platform. Some tours we run ourselves; for others we connect you with hand-picked independent operators. By using this site or booking through us, you agree to these terms.
        </Section>

        <Section title="Who we are">
          <p>This site is operated by <b>Riga Party Weekend</b>, based in Riga, Latvia. Contact: <a href="mailto:rigapartyweekend@rigapartyweekend.eu" className="underline underline-offset-4">rigapartyweekend@rigapartyweekend.eu</a>.</p>
        </Section>

        <Section title="Our role">
          <p>Riga Party Weekend acts in two ways:</p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li>For tours we run ourselves, we are the operator and these terms apply directly.</li>
            <li>For tours operated by partners, we act as an intermediary — we help you select, inquire and book, but the partner is responsible for delivering the actual experience and may have additional terms which we'll share before confirmation.</li>
          </ul>
        </Section>

        <Section title="Inquiries vs bookings">
          <p>Submitting our inquiry form is not a confirmed booking. We'll reply by email with a quote based on your group size, dates and any add-ons. A booking is only confirmed once you accept the quote and any required deposit has been paid.</p>
        </Section>

        <Section title="Pricing">
          <p>Prices on the website are starting prices ("from") per person unless otherwise stated. Final price depends on group size, season, duration and add-ons. EUR (€) is our standard currency. All prices include applicable Latvian VAT.</p>
        </Section>

        <Section title="Cancellations & refunds">
          <p>Cancellation terms can vary by tour and are confirmed in writing with your quote. As a general guideline:</p>
          <ul className="list-disc pl-5 space-y-2 mt-3">
            <li><b>More than 7 days before the tour:</b> full refund (minus payment-processing fees if applicable).</li>
            <li><b>3 – 7 days before:</b> 50% refund.</li>
            <li><b>Less than 72 hours before:</b> no refund.</li>
            <li><b>If we (or the operator) cancel</b> for any reason — including weather or safety — you get a full refund or the option to reschedule.</li>
          </ul>
          <p className="mt-3">The exact terms for your booking will be in your confirmation email and take precedence over the above guideline.</p>
        </Section>

        <Section title="Weather & safety">
          <p>Our tours run in many weather conditions. However, for safety reasons the operator may cancel or reschedule a tour with short notice. The decision of the operator is final. If we cancel, you receive a full refund or the option to move to another date or tour of equivalent value.</p>
        </Section>

        <Section title="Behaviour & alcohol">
          <p>Tours involve water, equipment, and other guests. Participants must follow the operator's safety instructions at all times. The operator may refuse participation, with no refund, if a guest is intoxicated, behaves aggressively, or otherwise puts themselves or others at risk. Light alcohol consumption may be acceptable on some tours but not others — check with us.</p>
        </Section>

        <Section title="Age, weight & health">
          <p>Many tours have minimum age, weight or height limits (e.g. water bikes have a 110 kg per-person weight limit; some tours are recommended for ages 10+). These are confirmed at the inquiry stage. By participating you confirm that all members of your group meet the requirements and are in suitable health for the activity. Pregnant guests and those with serious medical conditions should consult us before booking.</p>
        </Section>

        <Section title="Liability">
          <p>Participation in our tours is voluntary and at your own risk. We strongly recommend personal travel and accident insurance. To the maximum extent allowed under Latvian law, Riga Party Weekend's liability is limited to the amount paid for the tour. We are not liable for indirect losses (e.g. missed flights, lost luggage, follow-on bookings).</p>
        </Section>

        <Section title="Intellectual property">
          <p>All text, photographs, logos and other content on this website are the property of Riga Party Weekend or our partners and are protected by copyright. Please don't reuse them without written permission.</p>
        </Section>

        <Section title="Changes to these terms">
          <p>We may update these terms occasionally. The version that applies to your booking is the version in force on the day you submitted your inquiry.</p>
        </Section>

        <Section title="Governing law">
          <p>These terms are governed by the laws of the Republic of Latvia. Any disputes that can't be resolved directly will be settled by the courts of Riga, Latvia.</p>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-display text-2xl sm:text-3xl mb-4">{title}</h2>
      <div className="text-[var(--ink)] space-y-3">{typeof children === "string" ? <p>{children}</p> : children}</div>
    </div>
  );
}
